import { useContext } from 'react';

import { FeatureFlags } from '../../common/constants/featureFlags';
import {
  getCarrierRole,
  isNonCarrierRolePresent,
} from '../../common/constants/roles';
import { IDataGridColumn } from '../../common/interfaces/IDataGridColumn';
import { AuthContext } from '../../features/login/AuthWrapper';
import { FeatureFlagsContext } from '../featureFlagsWrapper';

/**
 * Custom hook that returns an array of column configurations for a data grid.
 * The columns are filtered based on user roles and visibility settings.
 *
 * @returns {IDataGridColumn[]} An array of column configurations with properties like name,
 * label copy, flex basis, interactivity, and sortability. Columns with isVisible set to
 * false are filtered out.
 */
export const useColumns = (): IDataGridColumn[] => {
  const auth = useContext(AuthContext);
  const isNonCarrierRole = isNonCarrierRolePresent(auth?.roles);
  const isCarrierRole = getCarrierRole(auth?.roles);
  const { flags } = useContext(FeatureFlagsContext);

  const columns: IDataGridColumn[] = [
    {
      name: 'customerDisplayName',
      labelCopy: 'applicant',
      flexBasis: '11.5%',
      width: '200px',
    },
    {
      name: 'carrierPolicyNumber',
      labelCopy: 'policy ID',
      flexBasis: '8%',
      width: '150px',
    },
    {
      name: 'product',
      flexBasis: '10%',
      width: '150px',
    },
    {
      name: 'applicationSla',
      labelCopy: 'SLA',
      flexBasis: '4%',
      interactive: true,
      sortable: !auth.prioritizedView,
      width: '150px',
    },
    {
      name: 'lastDecisionType',
      labelCopy: 'decision',
      flexBasis: '6%',
      width: '150px',
    },
    {
      name: 'appliedFor',
      labelCopy: 'coverage',
      flexBasis: '8%',
      width: '150px',
    },
    {
      name: 'lifecycle',
      interactive: true,
      flexBasis: '10.5%',
      width: '150px',
      isVisible:
        !flags?.readFromLifecycleV2Enabled && isCarrierRole !== undefined,
    },
    {
      name: 'detailedLifecycle',
      labelCopy: 'Detailed Lifecycle',
      interactive: true,
      flexBasis: '10.5%',
      width: '100px',
      isVisible: flags?.readFromLifecycleV2Enabled,
    },
    {
      name: 'stage',
      interactive: true,
      flexBasis: '10.5%',
      width: '150px',
      isVisible: isNonCarrierRole || flags?.readFromLifecycleV2Enabled,
    },
    {
      name: 'forReview',
      labelCopy: 'for review',
      flexBasis: '10%',
      width: '150px',
    },
    {
      name: 'creationDate',
      labelCopy: 'date created',
      interactive: true,
      sortable: !auth.prioritizedView,
      flexBasis: '12%',
      width: '150px',
    },
    {
      name: 'underwriterDisplayName',
      labelCopy: isCarrierRole ? 'carrier assignee' : 'underwriter',
      flexBasis: '10%',
      width: '150px',
    },
    {
      name: 'caseManagerDisplayName',
      labelCopy: 'case manager',
      flexBasis: '10%',
      width: '150px',
    },
    {
      name: 'agencyName',
      labelCopy: 'agency name',
      flexBasis: '10%',
      width: '150px',
      isVisible: flags[FeatureFlags.UW_NORA_AGENCY_SEARCH_ENABLED] || false,
    },
  ];

  return columns.filter(column => column.isVisible !== false);
};
